/**
 * Tracker d'etat lu/non-lu cote client (localStorage).
 *
 * Le backend Cyrano ne tracke pas (encore) la lecture par operateur — on
 * persiste donc en localStorage, scope cockpit. Forme du store :
 *
 *   {
 *     [conversationId]: {
 *       lastReadAt: number,          // ms epoch quand l'operateur a vu pour la derniere fois
 *       messageCountAtRead: number,  // snapshot du message_count au moment du read
 *     }
 *   }
 *
 * Semantique :
 *   - Pas de record → conv jamais ouverte → unread = message_count (toutes
 *     unread). Coherent avec Discord : on voit la pastille meme sur les
 *     channels jamais ouverts.
 *   - Record present → unread si last_message_at > lastReadAt, count =
 *     max(0, message_count - messageCountAtRead).
 *
 * Comportement FanPanel :
 *   - Snapshot a l'open (cf useFrozenUnreadAnchor) : la ligne rouge / le
 *     stripe jaune restent stables pendant la session de lecture, meme
 *     apres click "Mark as read".
 *   - Auto mark-read au unmount du panel (depart de la conv).
 */
import { atom, useAtom, useAtomValue } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback, useEffect, useRef } from "react";

import type { MockConversation } from "./mockData";

const STORAGE_KEY = "cockpit:unread:v1";

interface ReadRecord {
  lastReadAt: number;
  messageCountAtRead: number;
}

export type UnreadStore = Record<string, ReadRecord>;

/** Persiste sur localStorage, hydrate au boot, sync entre panels. */
const unreadStoreAtom = atomWithStorage<UnreadStore>(STORAGE_KEY, {});

/** Setter dedie pour markRead — evite de re-binder useAtom partout. */
const markReadAtom = atom(
  null,
  (get, set, payload: { id: string; messageCount: number }) => {
    const next = { ...get(unreadStoreAtom) };
    next[payload.id] = {
      lastReadAt: Date.now(),
      messageCountAtRead: payload.messageCount,
    };
    set(unreadStoreAtom, next);
  },
);

/** Nombre de messages non lus pour UNE conversation — désormais PARTAGÉ équipe :
 *  on lit directement `conv.unread_count`, hydraté par le backend depuis le
 *  timestamp de lecture COMMUN (`admin_read:{id}`). Le store localStorage n'est
 *  plus la source du "lu" sur la grille — il ne sert qu'à positionner la ligne
 *  rouge "à partir d'ici non lu" du chat, par opérateur (useFrozenUnreadAnchor). */
export function getUnreadCount(conv: MockConversation): number {
  return conv.unread_count;
}

/** Hook pour la pastille / le grisé d'une card. Re-rend quand la conv change
 *  (cache react-query : optimisme du mark-read + refetch/poll de la liste). */
export function useUnreadCount(conv: MockConversation): number {
  return conv.unread_count;
}

/** Store lu/non-lu live, pour les consumers qui doivent raisonner sur
 *  PLUSIEURS conversations a la fois (ex: tri "non lus d'abord" au niveau
 *  de la grille). Combine avec `getUnreadCount(conv, store)`. Re-rend a
 *  chaque changement du store. */
export function useUnreadStore(): UnreadStore {
  return useAtomValue(unreadStoreAtom);
}

/** Hook pour marquer comme lu. Stable. */
export function useMarkRead(): (id: string, messageCount: number) => void {
  const [, mark] = useAtom(markReadAtom);
  return useCallback(
    (id: string, messageCount: number) => mark({ id, messageCount }),
    [mark],
  );
}

/** Snapshot fige du dernier read au mount du FanPanel. Sert a positionner
 *  la ligne rouge et le stripe jaune sans qu'ils sautent quand l'user
 *  click "Mark as read" en cours de session.
 *
 *  Retourne aussi `mountTime` : les messages arrivant APRES le mount sont
 *  consideres comme "live" (pas marques comme unread) — l'operateur est
 *  en train de regarder, pas de raison de surligner. */
export interface FrozenUnreadAnchor {
  /** ms epoch — borne inferieure : messages avec created_at > ca = unread */
  lastReadAt: number;
  /** ms epoch — borne superieure : messages apres = "live", pas unread */
  mountTime: number;
}

export function useFrozenUnreadAnchor(
  conversationId: string | undefined,
): FrozenUnreadAnchor | null {
  const store = useAtomValue(unreadStoreAtom);
  const ref = useRef<{ id: string; anchor: FrozenUnreadAnchor } | null>(null);

  if (!conversationId) {
    ref.current = null;
    return null;
  }
  // Re-snapshot uniquement quand la conv change (changement d'onglet Fan).
  if (!ref.current || ref.current.id !== conversationId) {
    const rec = store[conversationId];
    ref.current = {
      id: conversationId,
      anchor: {
        lastReadAt: rec?.lastReadAt ?? 0,
        mountTime: Date.now(),
      },
    };
  }
  return ref.current.anchor;
}

/** Auto-mark-as-read au depart de la conversation (unmount ou switch). */
export function useAutoMarkReadOnLeave(
  conversationId: string | undefined,
  messageCount: number,
): void {
  const markRead = useMarkRead();
  // Stocke le dernier message_count vu — on utilise cette valeur au unmount
  // (sinon le cleanup capturerait un count perime).
  const countRef = useRef(messageCount);
  countRef.current = messageCount;

  useEffect(() => {
    if (!conversationId) return;
    return () => {
      markRead(conversationId, countRef.current);
    };
  }, [conversationId, markRead]);
}
